import { Head, Link, router } from "@inertiajs/react"
import { MessageCircle, Plus, Trash2 } from "lucide-react"
import type { MouseEvent } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"
import { draftPath, draftsPath, newDraftPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface Draft {
  id: number
  name: string
  created_at: string
  updated_at: string
}

interface DraftsPageProps {
  drafts: Draft[]
}

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Drafts",
    href: draftsPath(),
  },
]

export default function Drafts({ drafts }: DraftsPageProps) {
  const handleDraftClick = (draftId: number) => {
    router.visit(draftPath(draftId))
  }

  const handleDelete = (e: MouseEvent, draftId: number) => {
    e.stopPropagation()
    router.delete(draftPath(draftId))
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />

      <div className="flex h-full min-h-0 flex-1 flex-col gap-4 overflow-x-auto rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Drafts</h1>
            <p className="text-muted-foreground">Manage and view your drafts</p>
          </div>
          <Button className="gap-2" asChild>
            <Link href={newDraftPath()}>
              <Plus className="h-4 w-4" />
              New Draft
            </Link>
          </Button>
        </div>

        {drafts && drafts.length > 0 ? (
          <div className="grid gap-4">
            {drafts.map((draft) => (
              <Card
                key={draft.id}
                className="hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => handleDraftClick(draft.id)}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MessageCircle className="text-muted-foreground h-4 w-4" />
                      <CardTitle className="text-base">{draft.name}</CardTitle>
                      <Badge variant="secondary" className="text-xs">
                        {formatDate(draft.updated_at)}
                      </Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => handleDelete(e, draft.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <CardDescription>
                    Created {formatDate(draft.created_at)}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="py-12 text-center">
            <CardContent>
              <MessageCircle className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
              <CardTitle className="mb-2">No drafts yet</CardTitle>
              <CardDescription className="mb-4">
                Start your first draft by clicking the button below.
              </CardDescription>
              <Button className="gap-2" asChild>
                <Link href={newDraftPath()}>
                  <Plus className="h-4 w-4" />
                  Create Your First Draft
                </Link>
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  )
}
